import { primitives } from './primitives';

/** Layer 2 — Semantic tokens (intent-based). */
export const semantic = {
  color: {
    brand: {
      primary: primitives.color.brand[500],
      primaryHover: primitives.color.brand[600],
      secondary: primitives.color.brand[400],
      subtle: primitives.color.brand[50],
      emphasis: primitives.color.brand[800],
      onPrimary: primitives.color.neutral[0],
    },
    text: {
      primary: primitives.color.textLegacy,
      secondary: primitives.color.textMutedLegacy,
      tertiary: primitives.color.neutral[400],
      inverse: primitives.color.neutral[0],
      brand: primitives.color.brand[500],
      disabled: primitives.color.neutral[300],
    },
    surface: {
      page: primitives.color.pageBg,
      raised: primitives.color.neutral[0],
      overlay: primitives.color.neutral[100],
      sunken: primitives.color.neutral[50],
      inverse: primitives.color.footer,
    },
    border: {
      subtle: primitives.color.neutral[100],
      default: primitives.color.neutral[150],
      strong: primitives.color.neutral[300],
      brand: primitives.color.brand[500],
      focus: primitives.color.brand[500],
    },
    /** Modais e sobreposições (admin) */
    overlay: {
      modalBackdrop: `color-mix(in srgb, ${primitives.color.neutral[900]} 50%, transparent)`,
    },
    feedback: {
      success: primitives.color.green[500],
      successBg: primitives.color.green[50],
      successText: primitives.color.green[700],
      warning: primitives.color.amber[500],
      warningBg: primitives.color.amber[50],
      warningText: primitives.color.amber[700],
      error: primitives.color.red[500],
      errorBg: primitives.color.red[50],
      errorText: primitives.color.red[700],
      info: primitives.color.brand[500],
      infoBg: primitives.color.brand[50],
      infoText: primitives.color.brand[700],
    },
    /** Charts (e.g. Recharts) — align with brand / feedback palette */
    chart: {
      grid: primitives.color.neutral[100],
      tick: primitives.color.neutral[400],
      seriesPrimary: primitives.color.brand[500],
      seriesSecondary: primitives.color.brand[400],
      seriesAccent: primitives.color.green[500],
    },
  },
  scrim: {
    light: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.2) 50%, transparent 100%)',
    medium: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.3) 60%, transparent 100%)',
    hero: 'linear-gradient(to right, rgba(0,0,0,0.55) 0%, transparent 70%)',
    /** Subtle inner-page hero (no photo): warm wash over page bg */
    innerHero:
      'linear-gradient(135deg, rgba(0, 102, 204, 0.06) 0%, rgba(245, 247, 250, 1) 45%, rgba(0, 77, 153, 0.08) 100%)',
  },
  focusRing: `0 0 0 3px rgba(0, 102, 204, 0.2)`,
  space: {
    xs: primitives.space[2],
    sm: primitives.space[3],
    md: primitives.space[4],
    lg: primitives.space[6],
    xl: primitives.space[8],
    '2xl': primitives.space[12],
    '3xl': primitives.space[16],
    '4xl': primitives.space[24],
    inlineGap: primitives.space[2],
    formFieldGap: primitives.space[4],
    cardPadding: primitives.space[5],
    sectionGap: primitives.space[16],
    sectionGapMobile: primitives.space[12],
    pagePadding: primitives.space[6],
    /** Max width for readable body / subtitle blocks (e.g. section intros) */
    readableMax: '560px',
    /** Main content grid width (header, footer, containers) */
    containerMax: '1200px',
  },
  text: {
    hero: {
      size: primitives.fluidFontSize.hero,
      weight: primitives.fontWeight.extrabold,
      lineHeight: primitives.lineHeight.tight,
      letterSpacing: primitives.letterSpacing.tighter,
    },
    h1: {
      size: primitives.fluidFontSize.h1,
      weight: primitives.fontWeight.bold,
      lineHeight: primitives.lineHeight.tight,
      letterSpacing: primitives.letterSpacing.tight,
    },
    h2: {
      size: primitives.fluidFontSize.h2,
      weight: primitives.fontWeight.bold,
      lineHeight: primitives.lineHeight.snug,
      letterSpacing: primitives.letterSpacing.tight,
    },
    h3: {
      size: primitives.fluidFontSize.h3,
      weight: primitives.fontWeight.semibold,
      lineHeight: primitives.lineHeight.snug,
    },
    /** Itinerary day row in trip «Programa» — UI subheading, not full document h3 scale */
    itineraryDayTitle: {
      size: primitives.fluidFontSize.itineraryDayTitle,
      weight: primitives.fontWeight.semibold,
      lineHeight: primitives.lineHeight.snug,
      letterSpacing: primitives.letterSpacing.tight,
    },
    h4: {
      size: primitives.fontSize.xl,
      weight: primitives.fontWeight.semibold,
      lineHeight: primitives.lineHeight.normal,
    },
    bodyLg: {
      size: primitives.fontSize.md,
      weight: primitives.fontWeight.regular,
      lineHeight: primitives.lineHeight.relaxed,
    },
    body: {
      size: primitives.fluidFontSize.body,
      weight: primitives.fontWeight.regular,
      lineHeight: primitives.lineHeight.relaxed,
    },
    bodySm: {
      size: primitives.fontSize.sm,
      weight: primitives.fontWeight.regular,
      lineHeight: primitives.lineHeight.normal,
    },
    label: {
      size: primitives.fontSize.sm,
      weight: primitives.fontWeight.semibold,
      lineHeight: primitives.lineHeight.normal,
    },
    caption: {
      size: primitives.fontSize.xs,
      weight: primitives.fontWeight.medium,
      lineHeight: primitives.lineHeight.normal,
    },
    price: {
      size: primitives.fontSize.lg,
      weight: primitives.fontWeight.bold,
      lineHeight: primitives.lineHeight.tight,
    },
    overline: {
      size: primitives.fontSize.xs,
      weight: primitives.fontWeight.bold,
      lineHeight: primitives.lineHeight.normal,
      letterSpacing: primitives.letterSpacing.widest,
    },
  },
} as const;

export type Semantic = typeof semantic;
